import { useEffect, useState } from "react";


// Tailwind's default breakpoints (https://tailwindcss.com/docs/responsive-design)
export const SCREEN_QUERIES = {
    isMobile: '(max-width: 639px)',
    isTablet: '(min-width: 640px) and (max-width: 1023px)',
    isDesktop: '(min-width: 1024px)',
    // Tailwind's md: where the rail appears and the mobile header stops.
    isTabletUp: '(min-width: 768px)',
    // Room for a document beside the content, not just beside the rail.
    isWide: '(min-width: 1280px)',
};

interface ScreenSizeMatches {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isTabletUp: boolean;
    isWide: boolean;
}

/**
 * React hook to track screen size breakpoints (using Tailwind's defaults).
 *
 * @returns An object with booleans: isMobile, isTablet, isDesktop,
 *
 * Example usage:
 *   const { isDesktop, isTablet, isMobile } = useCurrentScreenSize();
 */
export function useCurrentScreenSize(): ScreenSizeMatches {
    // Helper to get the current match state for all queries
    const getMatches = () => {
        const result: ScreenSizeMatches = {
            isMobile: false,
            isTablet: false,
            isDesktop: false,
            isTabletUp: false,
            isWide: false,
        };
        for (const key in SCREEN_QUERIES) {
            result[key] = typeof window !== 'undefined' ? window.matchMedia(SCREEN_QUERIES[key]).matches : false;
        }
        return result;
    };

    const [matches, setMatches] = useState(getMatches);

    useEffect(() => {
        // Create a MediaQueryList for each query
        const mqls: { mql: MediaQueryList; update: () => void }[] = [];

        // Update function to set matches state
        const update = () => setMatches(getMatches());

        // Add event listeners for each media query
        for (const key in SCREEN_QUERIES) {
            const mql = window.matchMedia(SCREEN_QUERIES[key]);
            mql.addEventListener('change', update);
            mqls.push({ mql, update });
        }

        // Initial check
        update();

        // Cleanup event listeners on unmount
        return () => {
            mqls.forEach(({ mql, update }) => mql.removeEventListener('change', update));
        };
    }, []);

    return matches;
}
